import './workerAssignmentReadiness.css'
import { useContext, useEffect, useState } from 'react'
import { AuthContext } from '../../../context/AuthContext'
import { useAccessContext } from '../../../context/AccessContext'
import { useI18n } from '../../../i18n/I18nContext'
import { getApiAuthToken } from '../../../services/api'
import { getWorkerAssignmentReadiness } from '../../../services/workerAssignmentReadiness'
import { acceptanceBlockerLabels, validateAssignmentReadiness, type AssignmentReadiness } from '../../../services/workerAssignmentReadinessModel'

export const WorkerAssignmentReadinessPanel = ({ assignmentId }: { assignmentId: string }) => {
  const { accessContext, selectedTenantId, loading } = useAccessContext()
  const auth = useContext(AuthContext)
  const ownerId = accessContext?.currentUser?.id
  const tenantId = selectedTenantId || accessContext?.tenantId
  if (loading || auth?.loading || !ownerId || auth?.user?.id !== ownerId || !tenantId || !assignmentId) return null
  return <Readiness key={`${ownerId}:${tenantId}:${assignmentId}`} ownerId={ownerId} tenantId={tenantId} assignmentId={assignmentId} />
}

const Readiness = ({ ownerId, tenantId, assignmentId }: { ownerId: string; tenantId: string; assignmentId: string }) => {
  const { t, formatDateTime } = useI18n()
  const [open, setOpen] = useState(false)
  const [refresh, setRefresh] = useState(0)
  const [snapshot, setSnapshot] = useState<AssignmentReadiness | null>(null)
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    setSnapshot(null)
    if (!open) { setLoading(false); return }
    const controller = new AbortController()
    const token = getApiAuthToken()
    let active = true
    setLoading(true)
    void getWorkerAssignmentReadiness(assignmentId, tenantId, controller.signal).then(payload => {
      if (active && token === getApiAuthToken()) setSnapshot(validateAssignmentReadiness(payload, ownerId, tenantId, assignmentId))
    }).catch(() => { /* Shared transport errors yield unknown guidance, never eligibility. */ }).finally(() => {
      if (active) setLoading(false)
    })
    return () => { active = false; controller.abort() }
  }, [open, refresh, ownerId, tenantId, assignmentId])
  return <details className="worker-assignment-readiness" onToggle={event => setOpen(event.currentTarget.open)}>
    <summary>{t('Acceptance check')}</summary>
    {open ? <div aria-live="polite" aria-busy={loading}>
      <p>{t(loading ? 'Checking assignment acceptance…' : snapshot?.canAccept === true
        ? 'At the last check, the assignment could be accepted. The server checks again when you accept.'
        : snapshot?.canAccept === false ? 'The assignment cannot currently be accepted. Review the reasons below.'
          : 'Acceptance check is unavailable. Refresh before relying on it.')}</p>
      {!loading && snapshot ? <>
        <p>{t('Last acceptance check: {time}', { time: formatDateTime(snapshot.checkedAt, { dateStyle: 'medium', timeStyle: 'short' }) })}</p>
        {snapshot.blockers.length ? <ul>{snapshot.blockers.map(code => <li key={code}>{t(acceptanceBlockerLabels[code])}</li>)}</ul> : null}
        {snapshot.notices.includes('company_approval_pending') ? <p>{t('Your acceptance still requires organization approval before confirmation.')}</p> : null}
      </> : null}
      <p>{t('Acceptance checks cover assignment state, job and shift availability, and schedule conflicts. They do not certify documents, skills or permission to start work.')}</p>
      <button type="button" className="btn btn-ghost" disabled={loading} onClick={() => setRefresh(value => value + 1)}>{t('Refresh acceptance check')}</button>
    </div> : null}
  </details>
}
